import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Select, Tooltip } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useGoogleFiles } from '../../../../api/google-integrations-client/google-integrations-client.hooks';
import type { GoogleFileLink, GoogleFileType } from '../../hooks/use-sheet-access-gate';
import { GOOGLE_FILE_TYPE_CONFIG } from '../google-file-type.config';

interface GoogleDocViewerProps {
  /** The Google files referenced in the current chat, in appearance order. */
  documents: GoogleFileLink[];
  /**
   * File id → revision count, bumped each time Flora edits that file. Folded
   * into both the iframe `key` (to remount the frame) and a `src` cache-buster
   * (so the remount actually re-fetches instead of replaying the browser-cached,
   * pre-edit preview). Files absent from the map render at revision 0.
   */
  documentRevisions?: Record<string, number>;
  /**
   * The file to show, when the parent owns the selection (e.g. a file picked
   * from the header Files menu). Falls back to the in-viewer choice, then the
   * first file, when absent or no longer referenced.
   */
  selectedFileId?: string;
  /** Called when the user switches files from the in-viewer select. */
  onSelectFile?: (fileId: string) => void;
}

/**
 * Build the read-only Google `/preview` embed url for a file. Google explicitly
 * allows `/preview` to be framed (unlike `/edit`), and it authenticates via the
 * viewer's own browser Google session — so it renders only for files the current
 * user can already open.
 *
 * @param {GoogleFileType} fileType - The Google file kind.
 * @param {string} fileId - The regex-validated file id (`[A-Za-z0-9_-]{20,}`).
 * @returns {string} The `https://docs.google.com/.../preview` url.
 */
function buildPreviewUrl(fileType: GoogleFileType, fileId: string): string {
  return `https://docs.google.com/${GOOGLE_FILE_TYPE_CONFIG[fileType].previewPath}/d/${fileId}/preview`;
}

/**
 * Single-file viewer for the chat's documents rail: a select to switch between
 * the Google files referenced in the conversation, and one read-only preview
 * that fills the rail for the chosen file. File names are resolved from the
 * accessible-files listing, falling back to a type label when a name is
 * unavailable (e.g. the file isn't in the listing yet).
 *
 * @param {GoogleDocViewerProps} props - The referenced files to offer and the optional parent-owned selection
 * @returns {JSX.Element | null} The viewer, or null when there are no files.
 */
export function GoogleDocViewer({
  documents,
  documentRevisions,
  selectedFileId: controlledFileId,
  onSelectFile,
}: GoogleDocViewerProps) {
  const { data: googleFiles } = useGoogleFiles(documents.length > 0);

  // Resolve each referenced file id to its display name from the listing.
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const file of googleFiles ?? []) {
      map.set(file.id, file.name);
    }
    return map;
  }, [googleFiles]);

  const labelFor = (link: GoogleFileLink): string =>
    nameById.get(link.fileId) ?? GOOGLE_FILE_TYPE_CONFIG[link.fileType].label;

  const [selectedFileId, setSelectedFileId] = useState<string | null>(documents[0]?.fileId ?? null);

  // Keep the selection valid as the referenced files change across turns.
  useEffect(() => {
    const stillPresent = documents.some((link) => link.fileId === selectedFileId);
    if (!stillPresent) {
      setSelectedFileId(documents[0]?.fileId ?? null);
    }
  }, [documents, selectedFileId]);

  const selected =
    documents.find((link) => link.fileId === (controlledFileId ?? selectedFileId)) ??
    documents.find((link) => link.fileId === selectedFileId) ??
    documents[0];

  if (!selected) {
    return null;
  }

  const config = GOOGLE_FILE_TYPE_CONFIG[selected.fileType];
  const previewUrl = buildPreviewUrl(selected.fileType, selected.fileId);
  const selectData = documents.map((link) => ({ value: link.fileId, label: labelFor(link) }));

  // After Flora edits a file its revision bumps; append it as a cache-buster so
  // the remounted iframe fetches a fresh preview URL instead of the browser's
  // cached pre-edit copy. Kept off the first load (revision 0) so the canonical
  // /preview URL is used until an edit actually happens. Google ignores the
  // unknown `rev` param, so it only serves to make the URL unique per revision.
  const revision = documentRevisions?.[selected.fileId] ?? 0;
  const frameSrc = revision > 0 ? `${previewUrl}?rev=${revision}` : previewUrl;

  return (
    <Viewer>
      <ViewerHeader>
        <Select
          data={selectData}
          value={selected.fileId}
          onChange={(value) => {
            if (!value) return;
            setSelectedFileId(value);
            onSelectFile?.(value);
          }}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true, zIndex: 1000 }}
          leftSection={<Icon icon={config.icon} width={16} height={16} />}
          aria-label="Select a file to preview"
          style={{ flex: 1, minWidth: 0 }}
        />
        <Tooltip label="Open in Google" position="top" withArrow>
          <OpenLink
            href={previewUrl.replace('/preview', '/edit')}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open in Google"
          >
            <Icon icon="material-symbols:open-in-new" width={16} height={16} />
          </OpenLink>
        </Tooltip>
      </ViewerHeader>
      <PreviewFrame
        key={`${selected.fileId}:${revision}`}
        src={frameSrc}
        title={`${labelFor(selected)} preview`}
        loading="lazy"
        referrerPolicy="no-referrer"
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      />
    </Viewer>
  );
}

const Viewer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  width: 100%;
`;

const ViewerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  flex-shrink: 0;
`;

const OpenLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-lighter);
  transition: background 0.15s, color 0.15s, border-color 0.15s;

  &:hover {
    border-color: var(--secondary-base);
    background: var(--secondary-lightest);
    color: var(--secondary-base);
  }
`;

const PreviewFrame = styled.iframe`
  display: block;
  width: 100%;
  flex: 1;
  min-height: 0;
  border: none;
  border-top: 1px solid var(--sky-light);
  background: var(--base-white);
`;
